import { useEffect, useState } from "react";

import { getAgentsAndTrackingNumbers, getSources } from "./services";

export default () => {
  const [agentsWithRoutingNumbers, setAgentsWithRoutingNumbers] = useState([]);
  const [trackingNumbers, setTrackingNumbers] = useState([]);
  const [accountOwnerId, setAccountOwnerId] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);

  const [sources, setSources] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const getAvailableSources = async () => {
      try {
        setLoadingSettings(true);
        const sourceStuff = getSources();
        const agentStuff = getAgentsAndTrackingNumbers();

        const [{ sources: available }, agentsssss] = await Promise.all([sourceStuff, agentStuff]);

        if (!cancelled) {
          const agentsMapped = mapAgentsWithRoutingNumber(agentsssss.agents);

          setAgentsWithRoutingNumbers(agentsMapped);
          setTrackingNumbers(mapTrackingNumbers(agentsssss.trackingNumbers, agentsMapped));
          setAccountOwnerId(agentsssss?.accountOwnerId || null);
          setSources(available);
        }
      } finally {
        setLoadingSettings(false);
      }
    };

    getAvailableSources();

    return () => {
      cancelled = true;
    };
  }, []);

  return { agentsWithRoutingNumbers, trackingNumbers, sources, accountOwnerId, loadingSettings };
};

const getSelectedFallback = (routingMethod, fallbackUserId, noAnswerSetting) => {
  if (routingMethod === "round_robin") {
    return noAnswerSetting;
  } else if (fallbackUserId) {
    return "call_individual";
  } else {
    return "voicemail";
  }
};

const getUsers = (trackingNumber, agentsById) => {
  let users = trackingNumber.tracking_number_routings.map((route) => ({
    id: route.user_id,
    label: agentsById[route.user_id]?.name,
    value: route.user_id,
    routingNumber: agentsById[route.user_id]?.routingNumber,
    name: agentsById[route.user_id]?.name,
    avatarUrl: agentsById[route.user_id]?.avatarUrl,
    position: route.position,
    isFallbackUser: false,
    isInboundSmsUser: false,
  }));

  // if fallback exists, append to users (if not already included)
  if (trackingNumber.fallback_user_id && agentsById[trackingNumber.fallback_user_id]) {
    const alreadyExists = users.some((u) => u.id === trackingNumber.fallback_user_id);
    if (!alreadyExists) {
      const fallbackAgent = agentsById[trackingNumber.fallback_user_id];
      users.push({
        id: trackingNumber.fallback_user_id || null,
        label: fallbackAgent.name || null,
        value: trackingNumber.fallback_user_id || null,
        routingNumber: fallbackAgent.routingNumber || null,
        name: fallbackAgent.name || null,
        avatarUrl: fallbackAgent.avatarUrl || null,
        position: users.length, // fallback marked as second last
        isFallbackUser: true,
        isInboundSmsUser: false,
      });
    }
  }

  // if inbound_sms_user_id exists, append to users (if not already included)
  if (trackingNumber.inbound_sms_user_id && agentsById[trackingNumber.inbound_sms_user_id]) {
    const alreadyExists = users.some((u) => u.id === trackingNumber.inbound_sms_user_id);
    if (!alreadyExists) {
      const inboundUserAgent = agentsById[trackingNumber.inbound_sms_user_id];
      users.push({
        id: trackingNumber.inbound_sms_user_id || null,
        label: inboundUserAgent.name || null,
        value: trackingNumber.inbound_sms_user_id || null,
        routingNumber: inboundUserAgent.routingNumber || null,
        name: inboundUserAgent.name || null,
        avatarUrl: inboundUserAgent.avatarUrl || null,
        position: users.length + 1, // inbound_sms_user_id marked as last
        isInboundSmsUser: true,
        isFallbackUser: false,
      });
    }
  }

  return users.toSorted((a, b) => a.position - b.position);
};

const mapTrackingNumbers = (trackingNumbers, agents) => {
  const agentsById = agents.reduce((acc, agent) => {
    acc[agent.id] = agent;

    return acc;
  }, {});

  return trackingNumbers.map((trackingNumber) => ({
    id: trackingNumber.id,
    name: trackingNumber.name,
    number: trackingNumber.number,
    routingMethod: trackingNumber.routing_rule,
    inboundRecording: trackingNumber?.inbound_recording || "disabled",
    inboundNotificationMessageCondition: trackingNumber?.inbound_notification_message_condition || "always",
    createLead: trackingNumber.create_leads,
    inboundSmsUserId: trackingNumber?.inbound_sms_user_id || null,
    source: trackingNumber.source_id,
    noAnswerSetting: trackingNumber.no_answer_setting,
    fallback: getSelectedFallback(
      trackingNumber.routing_rule,
      trackingNumber?.fallback_user_id || null,
      trackingNumber.no_answer_setting,
    ),
    fallbackUserId: trackingNumber?.fallback_user_id || null,
    voicemailGreeting: trackingNumber.voicemail_greeting,
    voicemailGreetingUrl: trackingNumber.voicemail_greeting_url,
    inboundRecording: trackingNumber.inbound_recording,
    inboundNotificationMessageCondition: trackingNumber.inbound_notification_message_condition,
    users: getUsers(trackingNumber, agentsById),
  }));
};

const mapAgentsWithRoutingNumber = (agents) => {
  const usable = [];
  agents.forEach((agent) => {
    usable.push({
      id: agent.id,
      name: agent.full_name,
      avatarUrl: agent.avatar_url.length > 3 ? agent.avatar_url : null,
      routingNumber:
        agent.routing_numbers?.find((rn) => agent.routing_number_id === rn.detail_id)?.routing_number || null,
      label: agent.full_name,
      value: agent.id,
      role: agent.role,
    });
  });

  return usable;
};
